import { Injectable } from "@angular/core";
import { Adapter } from "@core/adapter";
import { Employment, EmploymentAdapter } from "./employment.model";
import { AddressModel } from "../address.model";
import { OrganisationTeam, OrganisationTeamAdapter } from "./organisation-team";
import { RoleType } from "./organisation.model";

export interface OrganisationMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  communicationEmail: string;
  language:string;
  admin?: boolean;
  manager?: boolean;
  auditor?:boolean;
  image: string;
  profileId: string;
  organisationProfileId: string;
  gender:string;
  employeeNumber:string;
  dateOfBirth?:Date;
  placeOfBirth:string;
  nationalNumber:string;
  employments:Employment[];
  slNumber:string;
  smartLionsNumber:string|null;
  verified?:boolean;
  address:AddressModel;
  memberType?:'member'|'employee'|'ex-employee'|'non-employee'|'futur-employee'|'paternity_leave_full_time'|'paternity_leave_part_time';
  teams:OrganisationTeam[];
  managedTeams:OrganisationTeam[];
  hasActiveUser:boolean;
  roleIsReadOnly?:boolean;
  roleStart?:Date;
  roleEnd?:Date;
  advancedPermissions?:{
    canManageAdministrators:boolean;
    canManageWages:boolean;
    canManageAttachments:boolean;
    canManageCertificates:boolean;
    canAddColleaguesToRequest:boolean;
    // What they may do with the qualification catalogue; managing implies reading, and the API
    // reports it that way.
    canReadCertifications:boolean;
    canManageCertifications:boolean;
    canReadQualifications:boolean;
    canManageQualifications:boolean;
    canReceiveMailNotifications:boolean;
    canAccessTrainingRequests:boolean;
  }
  canGenerateNewAiAnalysis?: boolean;
  lastAiAnalysisResult?: {
    result?: {
      generatedAt?: string;
      recentCourses?: any[];
      recommended?: any[];
      summary?: string;
      traceId?: string;
      [key: string]: any;
    }
    [key: string]: any;
  }
  role:RoleType;
}


@Injectable({
  providedIn: 'root'
})

export class OrganisationMemberAdapter implements Adapter<OrganisationMember> {
  constructor(private employmentAdapter:EmploymentAdapter, private organisationTeamAdapter:OrganisationTeamAdapter) {
  }
  

  adapt(item: any): OrganisationMember {

    const employments:Employment[] = (item.employments ?? []).map((x:any) => this.employmentAdapter.adapt(x))   

    return {
      id: item.id,
      firstName: (item.userProfile.firstName || "").trim(),
      lastName: (item.userProfile.lastName || "").trim(),
      language:(item.userProfile.language??'').split('/').pop(),
      email: item.userProfile.emailAddress || "",
      admin: item.isAdmin ?? false,
      auditor:item.isAuditor ?? false,
      manager:item.isManager ?? false,
      roleIsReadOnly: item.isReadOnly, 
      image: "",
      profileId: item.userProfile.id || "",
      communicationEmail: item.userProfile.communicationEmailAddress || "",
      organisationProfileId: item.organisationProfile.id || "",
      gender:item.userProfile.gender || "",
      employeeNumber:item.employeeNumber || "",
      dateOfBirth:item.userProfile.dateOfBirth ?? null,
      placeOfBirth:item.userProfile.placeOfBirth??'',
      nationalNumber:item.nationalNumber??'',
      address:item.userProfile.domiciliaryAddress,
      employments: employments,
      memberType:item.status,
      slNumber:item.verificationCode??'',
      smartLionsNumber:item.userProfile.smartLionsNumber??null,
      verified:item.userVerified??false,
      teams:(item.teams??[]).map((t:any) => this.organisationTeamAdapter.adapt(t,false)),
      managedTeams:(item.managedTeams??[]).map((t:any) => this.organisationTeamAdapter.adapt(t,true)),
      roleStart:item.roleStart ?? null,
      roleEnd:item.roleEnd ?? null,
      advancedPermissions:item.advancedPermissions ? { ...item.advancedPermissions, canAccessTrainingRequests: item.advancedPermissions.canAccessTrainingRequests ?? true } : { canManageAdministrators:true, canAddColleaguesToRequest:false, canManageWages:true, canManageAttachments:false, canManageCertificates:true, canReadCertifications:true, canManageCertifications:true, canReadQualifications:true, canManageQualifications:true, canReceiveMailNotifications:true, canAccessTrainingRequests:true},
      lastAiAnalysisResult: item.lastAiAnalysisResult,
      canGenerateNewAiAnalysis: item.canGenerateNewAiAnalysis,
      hasActiveUser:item.active??false,
      role: this.setRoleType(item)
    }
  }

  setRoleType(item:any){
    if(item.isAdmin) return RoleType.ADMIN;
    if(item.isManager	&& !item.isReadOnly) return RoleType.MANAGER;
    if(item.isManager	&& item.isReadOnly) return RoleType.MANAGER_READONLY;
    if(item.isAuditor) return RoleType.AUDITOR;
    return RoleType.MEMBER;
  }

}